import type { APIState, Result } from '@api';
import { Success, Failure, useErrorMessage, useDatahubAPI } from '@api';
import { projectAtom } from '@data/auth';
import { defaultTimezone } from '@data/datahub/timezone/states';
import type { Timezone } from '@data/datahub/timezone/types';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type APIResponse = {
  timezone_data: Timezone;
};

export const useTimezoneAPI = (): {
  state: APIState;
  getTimezone: () => Promise<Timezone>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { datahubAPI } = useDatahubAPI();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
    ): Promise<Result<APIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/timezone`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getTimezone = useAtomCallback(
    useCallback(
      async (get) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_timezone', { status: 'failed' }),
          });
          setState('hasError');
          return defaultTimezone;
        }
        setState('loading');
        const res = await request(project.id, environment.environment_id);
        if (!res.value || res.isFailure()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((res as any).value?.status !== 404) {
            notifyError({
              message: `${t('api.datahub.get_timezone', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
          }
          setState('hasError');
          return defaultTimezone;
        }
        setState('hasValue');
        return res.value.timezone_data;
      },
      [notifyError, t, request, getErrorMessage],
    ),
  );

  return {
    state,
    getTimezone,
  };
};
